/**
 * 單據附件(REQ-20260907002)—— **跨單據共用**的附件表 zod schema 與常數。
 *
 * 與既有的 `purchase-order.schema.ts` 附件那一段(`PresignPurchaseAttachmentSchema` 等)
 * 的關係:那一組是「進貨單專屬」的舊入口,**保留不動**(列印頁與既有前端在用),
 * 後端已改成內部委派到這裡的 `document_attachments`,不留兩份真相。
 *
 * 三個與舊入口刻意不同的地方:
 *
 * 1. **多型關聯**:`docType` + `docId`。之後出貨單 / 報價單要貼檔案時,
 *    只要在 `DOCUMENT_TYPES` 加一筆 + 對應的權限判定,不必再開一張表。
 *    目前放行三種:`PURCHASE_ORDER` / `PURCHASE_ORDER_LINE` /
 *    `PURCHASE_ORDER_WORK_ORDER`(第三種是 REQ-20260917001 加的)—— 白名單而不是
 *    自由字串:docId 是別張表的主鍵,沒有白名單就等於讓呼叫端自己決定要掛到哪裡。
 *
 * 2. **檔案類型放寬成「圖片一律可以 + PDF」**。舊入口是寫死的三選一 enum
 *    (`image/jpeg` / `image/png` / `application/pdf`),現場用 HEIC / WebP 拍的單據
 *    就傳不上來。改成前綴判斷 —— 前後端共用 `isAllowedDocumentAttachmentMime()`
 *    這一支,不各寫一份 if。
 *
 * 3. **`category` 是選填標籤而不是必填值域**(需求原文:「上傳當下先存檔,
 *    類型可以後補。強制選類型會拖慢貼單據的動作」)。所以 create 不帶 category
 *    也合法,事後走 PATCH 補;清空也是合法動作(送 null)。
 *
 * ⚠️ 需求原文的欄位清單裡有 `file_url`,**這裡刻意沒有**:本專案的附件存在私有
 * MinIO bucket,對外一律發 5 分鐘 signed URL(見 `purchase-order-attachments.service`)。
 * 存死網址不是過期就是外洩,所以存的是 `storagePath`,網址由既有的 signed-url
 * 端點即時發。取捨寫在 REQ-20260907002 §2.1。
 */
import { z } from 'zod';

/**
 * 可以掛附件的單據類型(白名單)。值同時是 MinIO 的路徑前綴之一
 * (`documents/{docType}/{docId}/`),所以只能是英數與底線。
 *
 * `PURCHASE_ORDER_LINE` 的 docId 是 `purchase_order_lines.id`,不是進貨單 id ——
 * 明細列的現付收據就掛在這裡(需求原文:「明細列的現付收據…兩者不要混在同一個
 * UI 元件裡」)。刻意**不另開一張表、也不在這張表加 `line_id`**:多開一欄等於
 * 讓「這筆附件屬於誰」有兩個答案,而 docType 已經回答得了。
 *
 * `PURCHASE_ORDER_WORK_ORDER`(REQ-20260917001)的 docId 是**進貨單 id**,與
 * `PURCHASE_ORDER` 同一張單、同一組權限,只是分成兩個檔案櫃:廠商給的單據
 * (對帳單 / 發票 / 簽收單)與我們自己的工單 / 施工圖分開放,找起來才不用翻。
 * 走新的 docType 而不是加一個 `bucket` 欄位 —— 多型關聯本來就是為了這件事,
 * 而且 `documents/{docType}/{docId}/` 的路徑前綴跟著就分開了。
 * ⚠️ **不需要 migration**:`docType` 是 `VarChar(30)`,這個值 25 字元。
 */
export const DOCUMENT_TYPES = {
  PURCHASE_ORDER: '進貨單',
  PURCHASE_ORDER_LINE: '進貨單明細',
  PURCHASE_ORDER_WORK_ORDER: '工單附件',
} as const;

export type DocumentType = keyof typeof DOCUMENT_TYPES;

export const DOCUMENT_TYPE_KEYS = Object.keys(DOCUMENT_TYPES) as DocumentType[];

export function isDocumentType(value: string): value is DocumentType {
  return Object.prototype.hasOwnProperty.call(DOCUMENT_TYPES, value);
}

/**
 * 檔案類型標籤(需求原文指名的五種)。**選填** —— 上傳當下可以完全不指定,
 * 畫面顯示「未分類」,事後再 PATCH 補。
 *
 * 寫死成常數而不是做成設定頁的 reference table:需求指名就是這五個,
 * 而且它會被報表 / 查詢當成分類依據 —— 值域可自助新增的話,報表的欄位就跟著浮動。
 * (同 `UNIT_NAME_OPTIONS` 的取捨,CLAUDE.md §2)
 */
export const DOCUMENT_ATTACHMENT_CATEGORIES = {
  STATEMENT: '對帳單',
  INVOICE: '發票',
  SIGNED_RECEIPT: '簽收單',
  QUOTATION: '報價單',
  OTHER: '其他',
} as const;

export type DocumentAttachmentCategory = keyof typeof DOCUMENT_ATTACHMENT_CATEGORIES;

export const DOCUMENT_ATTACHMENT_CATEGORY_KEYS = Object.keys(
  DOCUMENT_ATTACHMENT_CATEGORIES,
) as DocumentAttachmentCategory[];

/** 單檔上限。**與既有的 `PURCHASE_ATTACHMENT_MAX_BYTES` 相同(10MB),需求沒有要改** */
export const DOCUMENT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

/** `<input accept>` 用的字串。與下面的 refine 是同一條規則的兩種寫法 */
export const DOCUMENT_ATTACHMENT_ACCEPT = 'image/*,application/pdf';

/**
 * 允許的 MIME:**任何圖片** + PDF。前後端共用這一支,不各寫一份 if。
 *
 * 為什麼不繼續用 enum:現場是拿手機拍單據,iOS 傳上來的是 `image/heic`、
 * 有些瀏覽器給 `image/webp` —— 三選一的 enum 會把它們擋在門外,而使用者看到的
 * 只是「不支援的檔案類型」。縮圖畫不出來時我們本來就有檔案圖示的 fallback,
 * 擋下來的損失遠大於放行。
 */
export function isAllowedDocumentAttachmentMime(mimeType: string): boolean {
  return mimeType.startsWith('image/') || mimeType === 'application/pdf';
}

const mimeType = z
  .string()
  .min(1)
  .max(100)
  .refine(isAllowedDocumentAttachmentMime, '只接受圖片或 PDF');

export const PresignDocumentAttachmentSchema = z.object({
  originalName: z.string().min(1).max(255),
  mimeType,
  sizeBytes: z.number().int().positive().max(DOCUMENT_ATTACHMENT_MAX_BYTES),
});
export type PresignDocumentAttachmentInput = z.infer<typeof PresignDocumentAttachmentSchema>;

export const CreateDocumentAttachmentSchema = PresignDocumentAttachmentSchema.extend({
  storedName: z.string().min(1),
  storagePath: z.string().min(1),
  /** 上傳當下不強制;沒帶就是「未分類」 */
  category: z.enum(DOCUMENT_ATTACHMENT_CATEGORY_KEYS as [string, ...string[]]).optional().nullable(),
});
export type CreateDocumentAttachmentInput = z.infer<typeof CreateDocumentAttachmentSchema>;

/**
 * 只改分類。**刻意只有這一個欄位** —— 檔名 / 大小 / 路徑都是上傳當下的事實,
 * 改了就對不回 MinIO 上那個物件。
 * `null` 是合法值(把已指定的類型清回未分類),所以 nullable 而不是只 optional。
 */
export const PatchDocumentAttachmentSchema = z.object({
  category: z.enum(DOCUMENT_ATTACHMENT_CATEGORY_KEYS as [string, ...string[]]).nullable(),
});
export type PatchDocumentAttachmentInput = z.infer<typeof PatchDocumentAttachmentSchema>;

export interface DocumentAttachmentResponse {
  id: number;
  uuid: string;
  docType: string;
  docId: number;
  /** null = 未分類(上傳當下沒選,或事後被清空) */
  category: string | null;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  /** 放大檢視的旋轉角度(REQ-20260909004)。0 = 沒轉過;檔案本身與 EXIF 不受影響 */
  rotation: number;
  uploadedAt: string;
}

export interface PresignDocumentUploadResponse {
  uploadUrl: string;
  storedName: string;
  storagePath: string;
  expiresInSec: number;
}
